"use client";

import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/shadcn-components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shadcn-components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/shadcn-components/ui/drawer";
import { cn } from "@/lib/utils";
import {
  calculateTradeAccountPnl,
  formatSignedPnl,
  formatOrderType,
  getTradeClosePrice,
  isMarketTrade,
  type OpenedTrade,
  useOpenedTrades,
  VybeOrderType,
} from "@/vybe-trader-client/opened-trades";
import { useVybeTraderClient } from "@/vybe-trader-client/connection/hooks/useVybeTraderClient";
import { useSubscribedSymbolsStore } from "@/vybe-trader-client/subscribed-symbols/stores/useSubscribedSymbolsStore";
import { useSymbolsStore } from "@/vybe-trader-client/symbols/stores/useSymbolsStore";
import { useAccountInformationStore } from "@/vybe-trader-client/account-information";

interface CloseByPositionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  position: OpenedTrade | null;
  isMobile?: boolean;
}

function getClosePrice(trade: OpenedTrade, quote?: { bid: number; ask: number }) {
  return getTradeClosePrice(trade, quote);
}

function PositionSummary({
  label,
  trade,
  selected,
  onClick,
}: {
  label: string;
  trade: OpenedTrade;
  selected?: boolean;
  onClick?: () => void;
}) {
  const quote = useSubscribedSymbolsStore(
    (state) => state.byCurrency.get(trade.symbol)?.quote
  );
  const allSubscribedSymbols = useSubscribedSymbolsStore(
    (state) => state.byCurrency
  );
  const allSymbols = useSymbolsStore((state) => state.byCurrency);
  const accountCurrency = useAccountInformationStore(
    (state) => state.account?.currency || "USD"
  );
  const closePrice = getClosePrice(trade, quote);
  const liveProfit = calculateTradeAccountPnl({
    trade,
    symbol: allSymbols.get(trade.symbol),
    quote,
    accountCurrency,
    symbols: allSymbols,
    quotes: allSubscribedSymbols,
  });
  const side = formatOrderType(trade.orderType).toLowerCase();
  const isBuy = trade.orderType === VybeOrderType.Buy;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={cn(
        "w-full text-left transition-colors",
        onClick && "hover:bg-muted/50",
        selected && "bg-orange-50 dark:bg-orange-950/20"
      )}
    >
      <div className="px-3 py-2">
        {label && (
          <div className="mb-1 text-xs font-semibold text-foreground">
            {label}
          </div>
        )}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold">
              #{trade.ticket}{" "}
              <span className={isBuy ? "text-blue-500" : "text-red-500"}>
                {side} {trade.lots}
              </span>
            </div>
            <div className="mt-0.5 text-sm text-muted-foreground">
              {trade.openPrice.toFixed(trade.digits)} →{" "}
              {closePrice.toFixed(trade.digits)}
            </div>
          </div>
          <div
            className={cn(
              "shrink-0 text-right text-lg font-bold",
              liveProfit >= 0 ? "text-blue-500" : "text-red-500"
            )}
          >
            {formatSignedPnl(liveProfit)}
          </div>
        </div>
      </div>
    </button>
  );
}

export default function CloseByPositionDialog({
  open,
  onOpenChange,
  position,
  isMobile = false,
}: CloseByPositionDialogProps) {
  const { allTrades } = useOpenedTrades();
  const { client } = useVybeTraderClient();
  const [selectedTicket, setSelectedTicket] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const quote = useSubscribedSymbolsStore((state) =>
    position ? state.byCurrency.get(position.symbol)?.quote : undefined
  );

  const candidates = useMemo(() => {
    if (!position) return [];
    const oppositeType =
      position.orderType === VybeOrderType.Buy
        ? VybeOrderType.Sell
        : VybeOrderType.Buy;

    return allTrades.filter(
      (trade) =>
        trade.ticket !== position.ticket &&
        trade.symbol === position.symbol &&
        trade.orderType === oppositeType &&
        isMarketTrade(trade)
    );
  }, [allTrades, position]);

  const selectedCandidate =
    candidates.find((trade) => trade.ticket === selectedTicket) ??
    candidates[0] ??
    null;

  useEffect(() => {
    if (!open) return;
    setSelectedTicket(candidates[0]?.ticket ?? null);
  }, [candidates, open, position?.ticket]);

  const handleCloseBy = async () => {
    if (!position || !selectedCandidate) return;
    if (!client || !client.isConnected()) {
      toast.error("Not connected to server");
      return;
    }

    setIsSubmitting(true);

    try {
      await client.closeOrderByHedge({
        Ticket: position.ticket,
        Symbol: position.symbol,
        Price: getClosePrice(position, quote),
        Lots: position.lots,
        OrderType: position.orderType,
        Slippage: 100,
        CloseByTicket: selectedCandidate.ticket,
      });

      toast.success("Close By Hedge submitted", {
        description: `Close #${position.ticket} by #${selectedCandidate.ticket}`,
      });
      onOpenChange(false);
    } catch (error) {
      toast.error("Close By Hedge failed", {
        description:
          error instanceof Error ? error.message : "Unable to close by hedge",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const content = (
    <div className="flex max-h-[78vh] flex-col overflow-hidden">
      {position ? (
        <>
          <div className="divide-y overflow-y-auto border-y">
            <PositionSummary label="Close position" trade={position} />

            {selectedCandidate ? (
              <>
                <div className="bg-muted/30 px-3 py-2 text-xs font-semibold">
                  By position
                </div>
                {candidates.map((candidate) => (
                  <PositionSummary
                    key={candidate.ticket}
                    label=""
                    trade={candidate}
                    selected={candidate.ticket === selectedCandidate.ticket}
                    onClick={() => setSelectedTicket(candidate.ticket)}
                  />
                ))}
              </>
            ) : (
              <div className="px-3 py-8 text-center text-sm text-muted-foreground">
                No opposite hedge position is available for {position.symbol}.
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 px-4 py-4 text-center">
            <div className="text-3xl font-bold text-red-500">
              {(quote?.bid ?? 0).toFixed(position.digits)}
            </div>
            <div className="text-3xl font-bold text-red-500">
              {(quote?.ask ?? 0).toFixed(position.digits)}
            </div>
          </div>

          <Button
            onClick={handleCloseBy}
            disabled={!selectedCandidate || isSubmitting}
            className="h-12 rounded-none bg-orange-500 text-base font-semibold text-white hover:bg-orange-600"
          >
            {selectedCandidate
              ? isSubmitting
                ? "Closing..."
                : `Close #${position.ticket} by #${selectedCandidate.ticket}`
              : "No hedge position available"}
          </Button>
        </>
      ) : (
        <div className="px-3 py-8 text-center text-sm text-muted-foreground">
          Select an open position to close by hedge.
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="max-h-[90vh]">
          <DrawerHeader className="pb-2">
            <DrawerTitle>Close By Hedge</DrawerTitle>
            <DrawerDescription>
              Pair this position with an opposite open position.
            </DrawerDescription>
          </DrawerHeader>
          {content}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0">
        <DialogHeader className="px-4 pt-4 text-left">
          <DialogTitle>Close By Hedge</DialogTitle>
          <DialogDescription>
            Pair this position with an opposite open position.
          </DialogDescription>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
}
